import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const StoreCashierGame = ({ gameData, onComplete }) => {
    const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [step, setStep] = useState('calculate_total'); // 'calculate_total', 'calculate_change', 'success'
    const [inputValue, setInputValue] = useState('');
    const [feedback, setFeedback] = useState(null);

    const currentScenario = gameData?.scenarios?.[currentScenarioIndex];

    const handleKeyPress = (val) => {
        if (feedback) return;
        triggerHaptic(30);
        
        if (val === 'C') {
            setInputValue('');
        } else if (val === '.') {
            if (!inputValue.includes('.')) {
                setInputValue(prev => prev + '.');
            }
        } else {
            if (inputValue.includes('.') && inputValue.split('.')[1].length >= 2) return;
            if (inputValue.length > 8) return;
            setInputValue(prev => prev + val);
        }
    };

    const checkAnswer = () => {
        if (feedback || !inputValue) return;
        const numInput = parseFloat(inputValue);
        
        if (step === 'calculate_total') {
            if (Math.abs(numInput - currentScenario.total) < 0.01) {
                setStep('calculate_change');
                setInputValue('');
                setFeedback(null);
                triggerHaptic(80);
            } else {
                triggerError('Suma neteisinga! 🛒');
            }
        } else if (step === 'calculate_change') {
            if (Math.abs(numInput - currentScenario.change) < 0.01) {
                setStep('success');
                const pointsToAdd = gameData.points_per_scenario || 20;
                const newScore = score + pointsToAdd;
                setScore(newScore);
                triggerHaptic([100, 50, 100]);
            } else {
                triggerError('Grąža neteisinga! 💳');
            }
        }
    };

    const triggerError = (msg) => {
        setFeedback({ type: 'error', message: msg });
        triggerHaptic([50, 50, 50]);
        setTimeout(() => setFeedback(null), 2000);
    };

    const nextScenario = () => {
        if (currentScenarioIndex < gameData.scenarios.length - 1) {
            setCurrentScenarioIndex(prev => prev + 1);
            setStep('calculate_total');
            setInputValue('');
            setFeedback(null);
            triggerHaptic(30);
        } else {
            triggerHaptic([200, 100, 200, 100, 300]);
            onComplete?.(score);
        }
    };

    if (!gameData || !gameData.scenarios || !currentScenario) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🏪</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Atidaroma parduotuvė...</h3>
            </div>
        );
    }

    const progress = (currentScenarioIndex / gameData.scenarios.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-indigo-100 rounded-xl md:rounded-3xl flex items-center justify-center text-xl md:text-4xl shadow-inner border-2 border-indigo-200 animate-float">
                        🛍️
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                            Išmanioji <span className="text-indigo-600">Kasa</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Apyvarta</p>
                        <p className="text-sm md:text-2xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[80px] md:min-w-[120px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Pirkėjas</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentScenarioIndex + 1}/{gameData.scenarios.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-8 w-full bg-slate-200/50 rounded-full h-2 md:h-4 p-0.5 md:p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-indigo-400 via-blue-500 to-teal-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(99,102,241,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.03] select-none pointer-events-none">🛒</div>
                <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.5em] mb-6 md:mb-10 text-center lg:text-left">Pirkinių Krepšelis</h4>
                
                <div className="space-y-3 md:space-y-6 max-h-[400px] overflow-y-auto pr-2 no-scrollbar">
                    {currentScenario.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 md:gap-6 bg-white/80 p-3 md:p-6 rounded-xl md:rounded-[2.5rem] shadow-xl border-2 border-slate-100 transition-all group">
                            <div className="text-3xl md:text-6xl drop-shadow-lg group-hover:animate-bounce">{item.emoji}</div>
                            <div className="flex-1">
                                <div className="font-black text-slate-900 text-sm md:text-2xl uppercase italic font-serif leading-none">{item.name}</div>
                            </div>
                            <div className="text-sm md:text-3xl font-black text-emerald-600 font-mono bg-emerald-50 px-4 py-1 md:px-6 md:py-2 rounded-xl border-2 border-emerald-100">{item.price.toFixed(2)}€</div>
                        </div>
                    ))}
                </div>
                
                {step === 'calculate_change' && (
                    <div className="mt-6 md:mt-10 pt-6 md:pt-10 border-t-4 md:border-t-8 border-dotted border-slate-200 animate-pop-in space-y-4 md:space-y-6">
                        <div className="flex justify-between items-center bg-slate-900 text-white p-4 md:p-8 rounded-2xl md:rounded-[2.5rem] border-4 border-slate-800 shadow-2xl">
                            <span className="text-[10px] md:text-sm font-black text-slate-400 uppercase tracking-widest">Bendra Suma</span>
                            <span className="text-xl md:text-5xl font-black font-mono">{currentScenario.total.toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between items-center bg-indigo-600 text-white p-4 md:p-8 rounded-2xl md:rounded-[3rem] border-4 border-indigo-400 shadow-xl">
                            <span className="text-[10px] md:text-sm font-black text-indigo-200 uppercase tracking-widest">Pirkėjas davė</span>
                            <span className="text-xl md:text-5xl font-black font-mono">{currentScenario.paid_amount.toFixed(2)}€</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 w-full max-w-4xl mx-auto py-4">
            <div className="premium-glass rounded-3xl md:rounded-[5rem] p-4 md:p-10 shadow-2xl border-white/40 bg-slate-950 relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 left-0 p-8 text-[150px] opacity-[0.02] select-none pointer-events-none text-white font-black">CASH</div>
                
                <div className="relative z-10 w-full flex flex-col items-center">
                    <div className="w-full bg-slate-900 rounded-2xl md:rounded-[3.5rem] p-6 md:p-12 border-4 border-slate-800 mb-6 md:mb-12 text-center shadow-inner relative overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(79,70,229,0.1)_0%,transparent_100%)] pointer-events-none" />
                        <h4 className="text-[8px] md:text-[10px] font-black text-indigo-400 uppercase tracking-[0.6em] mb-4">
                            {step === 'calculate_total' ? 'ĮVESKITE SUMĄ' : 'ĮVESKITE GRĄŽĄ'}
                        </h4>
                        <div className={`text-5xl md:text-8xl font-black font-mono transition-all duration-300 leading-none ${feedback ? 'text-rose-500 animate-shake' : 'text-white'}`}>
                            {inputValue || '0.00'}<span className="text-indigo-500 text-2xl md:text-5xl ml-2">€</span>
                        </div>
                        {feedback && <div className="mt-2 text-rose-400 font-black uppercase text-[10px] tracking-widest">{feedback.message}</div>}
                    </div>

                    <div className="grid grid-cols-3 gap-2 md:gap-4 w-full max-w-md">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, 'C'].map((btn) => (
                            <button
                                key={btn}
                                onClick={() => handleKeyPress(btn)}
                                className={`
                                    h-16 md:h-28 rounded-xl md:rounded-[2.5rem] text-xl md:text-4xl font-black flex items-center justify-center transition-all border-b-4 md:border-b-8
                                    ${btn === 'C' ? 'bg-rose-600 text-white border-rose-900 active:translate-y-1 active:border-b-0' : 
                                      btn === '.' ? 'bg-slate-800 text-white border-slate-900 active:translate-y-1 active:border-b-0' :
                                      'bg-white text-slate-900 border-slate-200 hover:bg-indigo-50 active:translate-y-1 active:border-b-0 shadow-lg'}
                                `}
                            >
                                {btn}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={checkAnswer}
                        disabled={!inputValue || !!feedback}
                        className={`
                            mt-6 md:mt-12 w-full max-w-md py-6 md:py-10 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl md:rounded-[3rem] font-black text-lg md:text-3xl shadow-2xl border-b-8 md:border-b-[12px] border-emerald-900 uppercase tracking-widest
                            ${inputValue && !feedback ? 'scale-105 active:translate-y-2 active:border-b-0' : 'opacity-20'}
                        `}
                    >
                        {step === 'calculate_total' ? 'TIKRINTI SUMĄ 🛒' : 'ATIDUOTI GRĄŽĄ 💳'}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {step === 'success' && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-3xl animate-fade-in" />
                    <div className="max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[12px] md:border-[16px] border-emerald-500 text-center">
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            🧾
                        </div>
                        
                        <div className="space-y-6 pt-4">
                            <h2 className="text-2xl md:text-5xl font-black uppercase tracking-tighter text-emerald-600 leading-none">
                                TRANZAKCIJA ATLIKTA!
                            </h2>
                            <div className="p-6 md:p-10 rounded-2xl md:rounded-[3rem] bg-emerald-50 border-4 border-emerald-100 shadow-inner italic font-serif">
                                <p className="text-sm md:text-2xl font-bold text-slate-700 leading-relaxed">
                                    "Pirkėjas žavisi tavo tikslumu! Kasa dirba idealiai, o pelnas auga!"
                                </p>
                            </div>
                            <button
                                onClick={nextScenario}
                                className="w-full py-6 md:py-10 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl md:rounded-[3rem] font-black text-lg md:text-3xl shadow-xl border-b-8 md:border-b-[12px] border-indigo-900 uppercase tracking-widest"
                            >
                                KITAS PIRKĖJAS ➡️
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StoreCashierGame;
